import { useMemo } from 'react';
import type { ChartDimensions, ChartPeriod, GroupedMetric } from '../types';
import { CHART_CONSTANTS } from '../constants';

export interface UseChartDimensionsOptions {
  periods: ChartPeriod[];
  height: number;
  hasComparison: boolean;
  hasMetricSelection: boolean;
  hasDualAxis: boolean;
  allGroupedMetrics: GroupedMetric[];
  maxValue: number;
}

/**
 * Calculates chart dimensions and positioning functions.
 */
export function useChartDimensions({
  periods,
  height,
  hasComparison,
  hasMetricSelection,
  hasDualAxis,
  allGroupedMetrics,
  maxValue,
}: UseChartDimensionsOptions): ChartDimensions {
  return useMemo(() => {
    const { PADDING, BAR_WIDTH } = CHART_CONSTANTS;

    const paddingLeft: number = PADDING.LEFT;
    const paddingTop: number = PADDING.TOP;
    const paddingBottom: number = hasComparison ? PADDING.BOTTOM_WITH_COMPARISON : PADDING.BOTTOM_DEFAULT;

    // Calculate right padding based on mode
    let paddingRight: number = PADDING.RIGHT_DEFAULT;
    if (hasComparison || hasMetricSelection) {
      paddingRight = hasDualAxis ? PADDING.RIGHT_DUAL_AXIS : PADDING.RIGHT_WITH_LEGEND;
    }

    // Calculate bar group width based on mode
    let barGroupWidth: number = BAR_WIDTH.SINGLE;
    if (hasComparison) {
      barGroupWidth = BAR_WIDTH.COMPARISON;
    } else if (hasMetricSelection) {
      barGroupWidth = BAR_WIDTH.METRIC;
    }

    const chartWidth = Math.max(periods.length * (barGroupWidth + 15) + paddingLeft + paddingRight, 500);
    const chartHeight = height - paddingTop - paddingBottom;

    const xStep = (chartWidth - paddingLeft - paddingRight) / periods.length;

    // Calculate bar width
    let barWidth: number;
    if (hasComparison) {
      barWidth = (barGroupWidth - 6) / 2;
    } else if (hasMetricSelection) {
      barWidth = (barGroupWidth - 4) / Math.max(allGroupedMetrics.length, 1);
    } else {
      barWidth = barGroupWidth * 0.7;
    }

    // Position calculation functions
    const getX = (index: number) => paddingLeft + index * xStep + xStep / 2;

    const getY = (value: number, axisMaxValue?: number) => {
      const effectiveMax = axisMaxValue || maxValue;
      return paddingTop + chartHeight - (value / effectiveMax) * chartHeight;
    };

    return {
      chartWidth,
      chartHeight,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      barGroupWidth,
      barWidth,
      xStep,
      getX,
      getY,
    };
  }, [periods.length, height, hasComparison, hasMetricSelection, hasDualAxis, allGroupedMetrics.length, maxValue]);
}
